/**
 * Hook aislado para gestionar favoritos / más usados / progreso del usuario.
 * Carga el resumen completo de "mis documentos" en una sola llamada al backend.
 *
 * Devuelve:
 *  - favoritos:        Set<manual_id>  (rápido para `has()`)
 *  - masUsados:        Array<manual_id>  (top 5 por aperturas, ya ordenado)
 *  - progresoPorManual: Object<manual_id, { ultima_pagina, aperturas, favorito, ... }>
 *  - toggleFavorito(manualId): cambia el estado de favorito y actualiza el cache local
 *  - reload(): vuelve a fetchear el resumen
 *  - loading: boolean
 */
import { useEffect, useState, useCallback } from 'react';
import { misDocumentosAPI } from '../services/api';

const useFavoritos = () => {
  const [favoritos, setFavoritos] = useState(new Set());
  const [masUsados, setMasUsados] = useState([]);
  const [progresoPorManual, setProgresoPorManual] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchResumen = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await misDocumentosAPI.getResumen();
      setFavoritos(new Set(data.favoritos || []));
      setMasUsados(data.mas_usados || []);
      setProgresoPorManual(data.progreso_por_manual || {});
    } catch (_err) {
      // Silencioso: si falla, simplemente queda vacío y la UI no muestra extras.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResumen();
  }, [fetchResumen]);

  const toggleFavorito = useCallback(
    async (manualId) => {
      // Optimistic update
      const nuevoEstado = !favoritos.has(manualId);
      setFavoritos((prev) => {
        const next = new Set(prev);
        if (nuevoEstado) next.add(manualId);
        else next.delete(manualId);
        return next;
      });

      try {
        await misDocumentosAPI.setFavorito(manualId, nuevoEstado);
      } catch (_err) {
        // Rollback ante error
        setFavoritos((prev) => {
          const next = new Set(prev);
          if (nuevoEstado) next.delete(manualId);
          else next.add(manualId);
          return next;
        });
      }
    },
    [favoritos]
  );

  return {
    favoritos,
    masUsados,
    progresoPorManual,
    toggleFavorito,
    reload: fetchResumen,
    loading,
  };
};

export default useFavoritos;
